import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from "@mui/material";
import CommentAttachments from "components/Tasks/CommentAttachments";
import CommentComposer from "components/Tasks/CommentComposer";
import PriorityBadge from "components/Tasks/PriorityBadge";
import SubtaskManager from "components/Tasks/SubtaskManager";
import TaskAssigneeAvatars from "components/Tasks/TaskAssigneeAvatars";
import ConfirmDialog from "components/Modals/ConfirmDialog";
import useKothar from "context/useKothar";
import { useMemo, useState } from "react";
import { FaTrash } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  useChangeTaskStatus,
  useDeleteTaskComment,
  usePostTaskComment,
  useTaskComments,
} from "utils/taskService";
import {
  buildActivityItems,
  extractMentionIds,
  formatRelativeTime,
  formatTaskDate,
  getDaysLeftLabel,
  getDeadlineInfo,
  getSubtaskProgress,
  getSubtasks,
  getSubtaskAssignee,
  getTaskAssignees,
  getTaskAssignableUsers,
  getTaskOwner,
  getTaskStatusColor,
  normalizeTaskUser,
  renderMentionSegments,
  TASK_STATUS_OPTIONS,
} from "utils/taskUtils";

/**
 * One linked task inside a module's Tasks tab. Mirrors the task detail page (status changes,
 * subtasks, discussion) in compact form; full management (reassign, collaborators, edit) stays
 * one click away on the real task page.
 */
const ModuleTaskCard = ({ task, onStatusChanged }) => {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const [statusMenuAnchor, setStatusMenuAnchor] = useState(null);
  const [commentText, setCommentText] = useState("");
  const [commentMentionIds, setCommentMentionIds] = useState([]);
  const [commentAttachments, setCommentAttachments] = useState([]);
  const [commentPendingDeletion, setCommentPendingDeletion] = useState(null);

  const [{ usersList }] = useKothar();
  const currentUserEmail = localStorage.getItem("userEmail");
  const canChangeStatus = Boolean(task?.canChangeStatus);
  const canEditDetails = Boolean(task?.canEditDetails);

  const { data: comments = [], refetch: refetchComments } = useTaskComments(task?.id, {
    enabled: expanded && Boolean(task?.id),
  });

  const allAssignees = useMemo(() => getTaskAssignees(task), [task]);
  const taskAssigner = useMemo(() => normalizeTaskUser(task?.assignedBy), [task?.assignedBy]);
  const orgUsers = useMemo(() => {
    const everyone = usersList || [];
    const taskOrgId = task?.organizationId;
    if (!taskOrgId) return everyone;
    return everyone.filter((user) => (user?.organization?.id ?? user?.orgId) === taskOrgId);
  }, [usersList, task?.organizationId]);
  const mentionCandidates = useMemo(() => {
    const byId = new Map();
    const add = (person) => {
      if (person?.id != null && !byId.has(person.id)) byId.set(person.id, person);
    };
    allAssignees.forEach(add);
    add(taskAssigner);
    getSubtasks(task).forEach((subtask) => add(getSubtaskAssignee(subtask)));
    return orgUsers.filter((user) => byId.has(user.id));
  }, [allAssignees, taskAssigner, orgUsers, task]);
  const subtaskAssignableUsers = useMemo(
    () =>
      getTaskAssignableUsers({
        people: [...allAssignees, taskAssigner].filter(Boolean),
        directory: orgUsers || [],
      }),
    [allAssignees, taskAssigner, orgUsers]
  );
  const nameByEmail = useMemo(() => {
    const map = new Map();
    const add = (person) => {
      if (person?.email && person?.name) map.set(person.email.toLowerCase(), person.name);
    };
    (usersList || []).forEach(add);
    allAssignees.forEach(add);
    add(taskAssigner);
    return map;
  }, [usersList, allAssignees, taskAssigner]);
  const displayPersonName = (email) =>
    (email && nameByEmail.get(String(email).toLowerCase())) || email || "Unknown";

  const activityComments = useMemo(
    () => buildActivityItems(task?.changeLogs, comments).filter((item) => item.kind === "comment"),
    [task?.changeLogs, comments]
  );
  const subtaskProgress = useMemo(() => getSubtaskProgress(task), [task]);
  const deadlineInfo = useMemo(() => getDeadlineInfo(task), [task]);
  const daysLeftLabel = getDaysLeftLabel(deadlineInfo?.days);

  const { mutate: changeStatus, isLoading: isChangingStatus } = useChangeTaskStatus({
    onSuccess: () => {
      toast.success("Status updated");
      setStatusMenuAnchor(null);
      onStatusChanged?.();
    },
    onError: (error) =>
      toast.error(
        error?.response?.data?.errorMessage ||
          error?.response?.data?.message ||
          "Could not update the status"
      ),
  });

  const { mutate: postComment } = usePostTaskComment({
    onSuccess: () => {
      setCommentText("");
      setCommentMentionIds([]);
      setCommentAttachments([]);
      refetchComments();
    },
    onError: () => toast.error("Error posting comment"),
  });

  const { mutate: deleteComment } = useDeleteTaskComment({
    onSuccess: () => {
      setCommentPendingDeletion(null);
      refetchComments();
    },
    onError: () => {
      toast.error("Error deleting comment");
      setCommentPendingDeletion(null);
    },
  });

  const handleSubmitComment = () => {
    if (!commentText.trim() && commentAttachments.length === 0) return;
    const mentionIds = Array.from(
      new Set([...commentMentionIds, ...extractMentionIds(commentText, mentionCandidates)])
    );
    postComment({
      taskId: task.id,
      text: commentText.trim(),
      mentionedUserIds: mentionIds,
      attachments: commentAttachments,
    });
  };

  return (
    <div className="rounded-xl border border-app-border bg-app-surface overflow-hidden">
      <Accordion
        expanded={expanded}
        onChange={(_, next) => setExpanded(next)}
        disableGutters
        square={false}
        sx={{ boxShadow: "none" }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <div className="flex-1 min-w-0 pr-2">
            <p className="text-sm font-semibold text-app-text m-0 truncate">
              {task?.specificTask || "Untitled task"}
            </p>
            <div
              className="flex flex-wrap items-center gap-1.5 mt-1.5"
              onClick={(e) => e.stopPropagation()}
            >
              <Tooltip
                title={
                  canChangeStatus
                    ? "Change status"
                    : "Only people on this task can change its status"
                }
              >
                <span>
                  <Chip
                    label={task?.status || "-"}
                    size="small"
                    className={`capitalize font-semibold ${getTaskStatusColor(task?.status)}`}
                    onClick={
                      canChangeStatus ? (e) => setStatusMenuAnchor(e.currentTarget) : undefined
                    }
                    onDelete={
                      canChangeStatus ? (e) => setStatusMenuAnchor(e.currentTarget) : undefined
                    }
                    deleteIcon={
                      canChangeStatus ? <span className="text-[10px] pr-1">▾</span> : undefined
                    }
                    sx={canChangeStatus ? undefined : { cursor: "default" }}
                    disabled={isChangingStatus}
                  />
                </span>
              </Tooltip>
              <PriorityBadge priority={task?.priority} />
              {task?.deadline && (
                <Chip
                  size="small"
                  label={
                    daysLeftLabel
                      ? `${daysLeftLabel} · ${formatTaskDate(task.deadline)}`
                      : `Due ${formatTaskDate(task.deadline)}`
                  }
                  color={
                    deadlineInfo?.isPassed
                      ? "error"
                      : deadlineInfo?.isDueSoon
                      ? "warning"
                      : "default"
                  }
                  variant={deadlineInfo?.isPassed ? "filled" : "outlined"}
                />
              )}
              {subtaskProgress.total > 0 && (
                <Chip
                  size="small"
                  variant="outlined"
                  label={`${subtaskProgress.done}/${subtaskProgress.total} subtasks`}
                />
              )}
              <TaskAssigneeAvatars people={allAssignees} />
            </div>
          </div>
        </AccordionSummary>
        <AccordionDetails>
          <div className="space-y-4">
            {task?.comment ? (
              <p className="text-sm text-app-text whitespace-pre-wrap m-0">{task.comment}</p>
            ) : (
              <p className="text-sm text-app-text-muted m-0">No description provided.</p>
            )}

            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-app-text-secondary">
              <span>
                Owner: <strong className="text-app-text">{getTaskOwner(task)?.name ?? "-"}</strong>
              </span>
              {task?.assignedBy?.name && (
                <span>
                  Assigned by: <strong className="text-app-text">{task.assignedBy.name}</strong>
                </span>
              )}
              {task?.updatedDate && <span>Updated {formatRelativeTime(task.updatedDate)}</span>}
            </div>

            <div>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                Subtasks
                {subtaskProgress.total > 0
                  ? ` (${subtaskProgress.done} of ${subtaskProgress.total} done)`
                  : ""}
              </Typography>
              <SubtaskManager
                mode="live"
                subtasks={task?.subtasks || []}
                taskId={task.id}
                users={subtaskAssignableUsers}
                canManage={canEditDetails}
                canAddSubtask={canChangeStatus}
                currentUserEmail={currentUserEmail}
                taskCreatedDate={task?.createdDate || null}
              />
            </div>

            <div>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                Discussion{activityComments.length > 0 ? ` (${activityComments.length})` : ""}
              </Typography>
              <div className="space-y-2 mb-2 max-h-72 overflow-y-auto pr-1">
                {activityComments.length === 0 ? (
                  <p className="text-sm text-app-text-muted text-center py-4 m-0">
                    No comments yet — start the discussion below.
                  </p>
                ) : (
                  activityComments.map((item, idx) => (
                    <div
                      key={`c-${item.id ?? idx}`}
                      className="group rounded-lg border border-app-border bg-app-surface-muted p-3"
                    >
                      <div className="flex items-center justify-between mb-1">
                        <Tooltip title={item.postedBy || ""}>
                          <span className="text-sm font-semibold text-app-text">
                            {displayPersonName(item.postedBy)}
                          </span>
                        </Tooltip>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-app-text-muted">
                            {formatRelativeTime(item.date)}
                          </span>
                          {item.postedBy === currentUserEmail && (
                            <IconButton
                              size="small"
                              className="opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
                              onClick={() => setCommentPendingDeletion(item)}
                            >
                              <FaTrash className="text-xs text-app-text-secondary" />
                            </IconButton>
                          )}
                        </div>
                      </div>
                      {item.text && (
                        <p className="text-sm text-app-text whitespace-pre-wrap m-0">
                          {renderMentionSegments(item.text).map((seg, i) =>
                            seg.isMention ? (
                              <span key={i} className="text-primary font-medium">
                                {seg.text}
                              </span>
                            ) : (
                              <span key={i}>{seg.text}</span>
                            )
                          )}
                        </p>
                      )}
                      <CommentAttachments attachments={item.attachments || []} />
                    </div>
                  ))
                )}
              </div>
              <CommentComposer
                text={commentText}
                onTextChange={setCommentText}
                mentionedUserIds={commentMentionIds}
                onMentionedUserIdsChange={setCommentMentionIds}
                users={mentionCandidates}
                attachments={commentAttachments}
                setAttachments={setCommentAttachments}
                onSubmit={handleSubmitComment}
              />
            </div>

            <div className="flex justify-end">
              <Button
                size="small"
                variant="outlined"
                startIcon={<OpenInNewIcon fontSize="small" />}
                onClick={() => navigate(`/admin/tasks/view/${task.id}`)}
              >
                View in Tasks
              </Button>
            </div>
          </div>
        </AccordionDetails>
      </Accordion>

      <Menu
        anchorEl={statusMenuAnchor}
        open={Boolean(statusMenuAnchor)}
        onClose={() => setStatusMenuAnchor(null)}
      >
        {TASK_STATUS_OPTIONS.map((option) => (
          <MenuItem
            key={option.value}
            selected={option.value === task?.status}
            onClick={() => changeStatus({ id: task.id, status: option.value })}
          >
            {option.label}
          </MenuItem>
        ))}
      </Menu>

      <ConfirmDialog
        open={commentPendingDeletion != null}
        title="Delete this comment?"
        message="This cannot be undone."
        confirmLabel="Delete"
        confirmColor="error"
        onConfirm={() => deleteComment({ taskId: task.id, commentId: commentPendingDeletion?.id })}
        onCancel={() => setCommentPendingDeletion(null)}
      />
    </div>
  );
};

export default ModuleTaskCard;
